/**
 * Google Analytics como en el sitio viejo:
 * - GA4 principal G-15KS5QR8VC en todo el sitio (script en index.html).
 * - Un GA4 adicional por acceso, solo en las secciones /lenguaje/:id/...:
 *     4 Comunidad  -> G-BDKP3YMLTE + GTM-T5WFFTN
 *     1 Estudiantes -> G-P5CTQFZ9P8
 *     2 English     -> G-P7P9H353VL
 * El UA-70469494-1 de las .aspx viejas era Universal Analytics (baja de Google en 2023), no se replica.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const GA_MAIN_ID = 'G-15KS5QR8VC';

const BY_LENGUAJE = {
  4: { ga: 'G-BDKP3YMLTE', gtm: 'GTM-T5WFFTN' },
  1: { ga: 'G-P5CTQFZ9P8' },
  2: { ga: 'G-P7P9H353VL' },
};

const configured = new Set([GA_MAIN_ID]);
let gtmLoaded = false;

function loadGtm(containerId) {
  if (gtmLoaded || !containerId) return;
  gtmLoaded = true;
  // Snippet estándar de Google Tag Manager
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== 'dataLayer' ? `&l=${l}` : '';
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', containerId);
}

function lenguajeIdFromPath(pathname) {
  const m = pathname.match(/^\/lenguaje\/(\d+)/);
  return m ? Number(m[1]) : null;
}

/** Envía page_view en cada navegación SPA y activa el GA4/GTM del acceso cuando corresponde. */
export function AnalyticsTracker() {
  const location = useLocation();
  const firstRef = useRef(true);

  useEffect(() => {
    const { pathname } = location;
    if (pathname.startsWith('/admin')) return;
    if (typeof window.gtag !== 'function') return;

    const isFirst = firstRef.current;
    firstRef.current = false;

    const extra = BY_LENGUAJE[lenguajeIdFromPath(pathname)];
    let justConfigured = null;
    if (extra?.ga && !configured.has(extra.ga)) {
      // El config inicial ya emite el page_view de esta página para esta propiedad
      window.gtag('config', extra.ga);
      configured.add(extra.ga);
      justConfigured = extra.ga;
    }
    if (extra?.gtm) loadGtm(extra.gtm);

    // La primera vista ya la contó el config de index.html (y el del acceso si se acaba de configurar)
    if (isFirst) return;

    const sendTo = [...configured].filter((id) => id !== justConfigured);
    // setTimeout: deja que usePageMeta actualice el título antes de medir
    const t = setTimeout(() => {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_path: pathname,
        page_location: window.location.href,
        send_to: sendTo,
      });
    }, 0);
    return () => clearTimeout(t);
  }, [location]);

  return null;
}
